import React from 'react';
import { SiteFooter } from '@/components/landing/SiteFooter';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, User, ChevronRight, Home } from 'lucide-react';
import { blogPosts } from '@/data/blogPosts';
import { format, parseISO } from 'date-fns';
import SEOHead from '@/components/SEOHead';
import { breadcrumbSchema } from '@/lib/seoSchemas';

export default function BlogPage() {
  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead
        title="Schedule Guru Blog | Tournament Scheduling Guides & Best Practices"
        description="Practical guides on tournament scheduling, league management, and sports analytics — round robin, pool play, elimination brackets, and basketball stats coverage."
        path="/blog"
        type="website"
        jsonLd={breadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Blog', url: '/blog' },
        ])}
      />
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="/logo-transparent.png" 
                alt="Schedule Guru" 
                className="h-10"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/" className="text-slate-600 hover:text-[#00247D] flex items-center gap-1">
                <Home className="w-4 h-4" />
                Home
              </Link>
              <Link to="/pricing" className="text-slate-600 hover:text-[#00247D]">Pricing</Link>
              <Link to="/create" className="text-slate-600 hover:text-[#00247D]">Create Schedule</Link>
            </nav>
            <Link to="/create">
              <Button className="bg-[#00247D] hover:bg-[#001A5C]">
                Get Started Free
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#00247D] to-[#001A5C] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 
            className="text-4xl md:text-5xl font-bold mb-4"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            SCHEDULE GURU BLOG
          </h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            Tips, guides, and best practices for tournament scheduling. Learn how our free round robin schedule generator can transform your league management.
          </p>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {blogPosts.map((post) => (
            <Link key={post.id} to={`/blog/${post.id}`}>
              <Card className="h-full border-slate-200 hover:shadow-lg transition-shadow cursor-pointer group overflow-hidden">
                <div className="h-48 relative overflow-hidden bg-gradient-to-br from-[#00247D] to-[#CE1126]">
                  {post.image && (
                    <img
                      src={post.image}
                      alt={post.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
                  <span
                    className="absolute bottom-3 left-4 text-white text-sm font-bold tracking-wider"
                    style={{ fontFamily: 'Barlow Condensed' }}
                  >
                    {post.category.toUpperCase()}
                  </span>
                </div>
                <CardContent className="p-6">
                  <Badge className="mb-3 bg-blue-100 text-[#00247D] hover:bg-blue-200">
                    {post.category}
                  </Badge>
                  <h2 className="text-xl font-bold text-slate-800 mb-2 group-hover:text-[#00247D] transition-colors line-clamp-2">
                    {post.title}
                  </h2>
                  <p className="text-slate-600 text-sm mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <div className="flex items-center gap-4">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {format(parseISO(post.date), 'MMM d, yyyy')}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {post.readTime}
                      </span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#00247D] opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-r from-[#00247D] to-[#001A5C] rounded-2xl p-8 md:p-12 text-center text-white">
          <h2 
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            READY TO TRY OUR FREE ROUND ROBIN SCHEDULE GENERATOR?
          </h2>
          <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
            Join thousands of league organizers who trust Schedule Guru for their tournament scheduling needs. Create professional schedules in minutes.
          </p>
          <Link to="/create">
            <Button size="lg" className="bg-white text-[#00247D] hover:bg-slate-100">
              Create Your Free Schedule
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </main>

      {/* Footer */}
      <SiteFooter />
    </div>
  );
}
